import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { 
  X, 
  Clock, 
  Wallet,
  AlertCircle,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const AutoPayModal = ({ biller, onClose }) => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [payOnDue, setPayOnDue] = useState(true);
  const [daysBefore, setDaysBefore] = useState(0);
  const [maxAmount, setMaxAmount] = useState("");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [step, setStep] = useState(1);
  const initializedRef = useRef(false);

  // Fetch wallets
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Fetch existing autopay settings
  const { data: existingSettings } = useQuery({
    queryKey: ["autopay", biller.biller_id],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/bills/autopay/${biller.biller_id}`);
        return response.data;
      } catch (e) {
        return null;
      }
    }
  });

  // Initialize from existing settings (only once)
  useEffect(() => {
    if (initializedRef.current) return;
    
    if (existingSettings) {
      initializedRef.current = true;
      setEnabled(existingSettings.enabled);
      setPayOnDue(existingSettings.pay_on_due_date);
      setDaysBefore(existingSettings.days_before_due);
      setMaxAmount(existingSettings.max_amount?.toString() || "");
      setSelectedWallet(existingSettings.wallet_id);
    } else if (wallets?.length > 0 && !selectedWallet) {
      const jmdWallet = wallets.find(w => w.currency === "JMD");
      setSelectedWallet(jmdWallet?.wallet_id || wallets[0].wallet_id);
    }
  }, [existingSettings, wallets, selectedWallet]);

  // Setup autopay mutation
  const setupMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/bills/autopay`, {
        biller_id: biller.biller_id,
        enabled: enabled,
        pay_on_due_date: payOnDue,
        days_before_due: daysBefore,
        max_amount: maxAmount ? parseFloat(maxAmount) : null,
        wallet_id: selectedWallet
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["autopay", biller.biller_id]);
      queryClient.invalidateQueries(["myBillers"]);
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to setup auto-pay");
    }
  });

  // Delete autopay mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`${API}/bills/autopay/${biller.biller_id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["autopay", biller.biller_id]);
      queryClient.invalidateQueries(["myBillers"]);
      toast.success("Auto-pay disabled");
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to disable auto-pay");
    }
  });

  const selectedWalletData = wallets?.find(w => w.wallet_id === selectedWallet);

  const formatCurrency = (amount, currency = "JMD") => {
    const symbols = { JMD: "J$", USD: "$", GBP: "£", CAD: "C$" };
    return `${symbols[currency] || currency}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-lg bg-card/95 backdrop-blur-2xl rounded-t-3xl sm:rounded-3xl 
          border-t sm:border border-white/10 p-6 animate-slide-up max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="autopay-modal"
      >
        {step === 1 && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg">Auto-Pay Settings</h3>
              <button 
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Biller Info */}
              <div className="glass rounded-2xl p-4">
                <p className="font-medium text-foreground">{biller.nickname || biller.provider_name}</p>
                <p className="text-xs text-muted-foreground">{biller.masked_account}</p>
              </div>

              {/* Enable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Enable Auto-Pay</p>
                  <p className="text-xs text-muted-foreground">Automatically pay this bill</p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="autopay-toggle"
                />
              </div>

              {enabled && (
                <>
                  {/* Payment Timing */}
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">When to pay</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setPayOnDue(true); setDaysBefore(0); }}
                        className={`p-3 rounded-xl border ${payOnDue && daysBefore === 0
                          ? "border-primary bg-primary/10" 
                          : "border-white/10 bg-white/5"
                        } text-sm`}
                      >
                        On due date
                      </button>
                      <button
                        onClick={() => { setPayOnDue(false); setDaysBefore(3); }}
                        className={`p-3 rounded-xl border ${!payOnDue || daysBefore > 0
                          ? "border-primary bg-primary/10" 
                          : "border-white/10 bg-white/5"
                        } text-sm`}
                      >
                        Before due date
                      </button>
                    </div>
                    
                    {(!payOnDue || daysBefore > 0) && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          value={daysBefore}
                          onChange={(e) => setDaysBefore(parseInt(e.target.value) || 0)}
                          className="input-glass w-20 text-center"
                          data-testid="days-before-input"
                        />
                        <span className="text-sm text-muted-foreground">days before due date</span>
                      </div>
                    )}
                  </div>

                  {/* Wallet Selection */}
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Pay from</p>
                    <div className="space-y-2">
                      {wallets?.map((wallet) => (
                        <button
                          key={wallet.wallet_id}
                          onClick={() => setSelectedWallet(wallet.wallet_id)}
                          className={`w-full p-3 rounded-xl border flex items-center gap-3 ${
                            selectedWallet === wallet.wallet_id
                              ? "border-primary bg-primary/10"
                              : "border-white/10 bg-white/5"
                          }`}
                        >
                          <Wallet className="w-5 h-5 text-muted-foreground" />
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium">{wallet.currency} Wallet</p>
                            <p className="text-xs text-muted-foreground">
                              Balance: {formatCurrency(wallet.available_balance, wallet.currency)}
                            </p>
                          </div>
                          {selectedWallet === wallet.wallet_id && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Max Amount */}
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Maximum auto-pay amount (optional)</p>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {selectedWalletData?.currency === "USD" ? "$" : "J$"}
                      </span>
                      <Input
                        type="number"
                        placeholder="No limit"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        className="input-glass pl-12"
                        data-testid="max-amount-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty for no limit. Bills exceeding this amount will require manual approval.
                    </p>
                  </div>

                  {/* Warning */}
                  <div className="glass rounded-2xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <p className="text-xs text-muted-foreground">
                      Make sure you have sufficient balance in your selected wallet. 
                      Failed auto-pay attempts will trigger a notification.
                    </p>
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  className="w-full btn-primary h-12"
                  onClick={() => setupMutation.mutate()}
                  disabled={setupMutation.isPending || (enabled && !selectedWallet)}
                  data-testid="save-autopay-btn"
                >
                  {setupMutation.isPending ? "Saving..." : enabled ? "Enable Auto-Pay" : "Save Settings"}
                </Button>
                
                {existingSettings && existingSettings.enabled && (
                  <Button
                    variant="outline"
                    className="w-full btn-secondary h-12 text-rose-400 hover:text-rose-300"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    data-testid="disable-autopay-btn"
                  >
                    Disable Auto-Pay
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Success Step */}
        {step === 2 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">
              {enabled ? "Auto-Pay Enabled!" : "Settings Saved"}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {enabled 
                ? `Your ${biller.provider_name} bill will be paid automatically${daysBefore > 0 ? ` ${daysBefore} days before the due date` : " on the due date"}.`
                : "Auto-pay has been disabled for this biller."
              }
            </p>
            <Button
              className="w-full btn-primary h-12"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoPayModal;
